// src/pages/Admin.jsx
// STATUS: active | UPDATED: 2026-09-19
// LOG:
//   2026-09-19  Manager review added, with the count that blocks stage four.
//   2026-09-18  The people count excludes agents.
//   2026-09-17  Notes added to the destinations.
//   2026-09-15  Questions and Leads added to the destinations. Both were
//               reachable only by typing the URL. New figure: answers of
//               Jazz's that have not changed anything yet.
//   2026-09-07  Rewritten. Live numbers instead of a hand written checklist.
//   2026-08-02  Rebuilt from stub into the operator command center.
//
// WHY THE CHECKLIST WENT
//
// It was a hardcoded list of setup tasks with hand maintained done flags, and
// it rotted. It listed "Add a DMARC record" as outstanding two days after we
// set the domain to p=quarantine, and "Add PSI_API_KEY to Netlify" five days
// after the key went in. A checklist that tells you to do things you finished
// teaches you to stop reading it.
//
// It was also written as notes between Tyler and me rather than as a product.
// Jazz opens this screen too, and both super admins should see the same thing:
// the state of the business, not a private to-do list.
//
// So it reads the database instead. Numbers cannot rot.
//
// NO CARDS
//
// Content sits on the page. Hierarchy comes from type size, weight and space,
// with a hairline rule where a real division exists. A border around every
// group is a box drawn to compensate for a layout that has not decided what
// matters, and on a phone it costs sixteen pixels of line length per side to
// say nothing.

import { useCallback, useEffect, useState } from 'react'
import { Box, Flex, HStack, VStack, Text, Icon, Spinner, SimpleGrid } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { ChevronRight, Store, TrendingUp, Users, ClipboardList, MessageSquareQuote, Inbox, FileText, ClipboardCheck } from 'lucide-react'
import { supabase } from '../lib/supabase'

const DESTINATIONS = [
  { to: '/admin/onboard/', icon: Store, title: 'Add a dealership',
    detail: 'Find the store, confirm the details, add its locations and invite its people.' },
  { to: '/admin/dealerships/', icon: ClipboardList, title: 'Dealerships',
    detail: 'Every store, its locations, its brand colours and its storefront reports.' },
  { to: '/admin/digital/', icon: TrendingUp, title: 'Janda Digital',
    detail: 'Score a storefront and send the dealer a report they can act on.' },
  { to: '/admin/team/', icon: Users, title: 'Everybody',
    detail: 'Every person with an account, grouped by the store they belong to.' },
  // Both of these were reachable only by knowing the URL. Leads because the
  // lead email links straight to it, questions because it did not exist.
  { to: '/admin/manager-review/', icon: ClipboardCheck, title: 'Your manager system',
    detail: 'Thirty nine lines from your Trainer Edition. Confirm, change or drop each one.' },
  { to: '/notes/', icon: FileText, title: 'Notes',
    detail: 'Reports and plans for Jazz, where he can read them and say what he thinks.' },
  { to: '/admin/questions/', icon: MessageSquareQuote, title: 'Questions',
    detail: 'What Jazz has answered, and whether we have built anything with it yet.' },
  { to: '/admin/leads/', icon: Inbox, title: 'Leads',
    detail: 'Everybody who has filled in the form on jandatraining.com.' },
]

// A number and what it means. No border, no fill: the figure is large enough to
// be the anchor on its own, which is what a card was being used to fake.
function Figure({ value, label, note, loading }) {
  return (
    <Box>
      <Text
        fontSize={{ base: '2.5rem', md: '3rem' }}
        fontWeight={600}
        letterSpacing="-0.03em"
        lineHeight={1}
        color="ink"
        fontVariantNumeric="tabular-nums"
      >
        {loading ? <Spinner size="md" color="inkMuted" thickness="2px" /> : value}
      </Text>
      <Text fontSize="body-sm" fontWeight={600} color="ink" mt={2}>
        {label}
      </Text>
      {note && (
        <Text fontSize="caption" color="inkMuted" mt={0.5} lineHeight={1.5}>
          {note}
        </Text>
      )}
    </Box>
  )
}

function Destination({ item }) {
  return (
    <HStack
      as={RouterLink}
      to={item.to}
      align="flex-start"
      spacing={4}
      py={5}
      borderTop="1px solid"
      borderColor="line"
      _hover={{ textDecoration: 'none', '& .go': { transform: 'translateX(3px)' } }}
    >
      <Icon as={item.icon} boxSize={5} color="inkMuted" mt={0.5} flexShrink={0} />
      <Box flex={1} minW={0}>
        <Text fontWeight={600} color="ink" letterSpacing="-0.01em">
          {item.title}
        </Text>
        <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6} mt={0.5}>
          {item.detail}
        </Text>
      </Box>
      <Icon
        as={ChevronRight}
        className="go"
        boxSize={4}
        color="inkDim"
        mt={1}
        flexShrink={0}
        transition="transform 0.15s ease"
      />
    </HStack>
  )
}

export default function Admin() {
  const [s, setS] = useState({ loading: true })

  const load = useCallback(async () => {
    const count = async (table, build) => {
      let q = supabase.from(table).select('id', { count: 'exact', head: true })
      if (build) q = build(q)
      const { count: n } = await q
      return n ?? 0
    }
    try {
      const [stores, locations, people, signoffs, waiting, confirmed, reports, coached, unapplied, mgrPending] =
        await Promise.all([
          count('dealerships'),
          count('locations'),
          // Otto is a users row and is not a person. Every figure that says
          // "people" has to say people.
          count('users', (q) => q.eq('is_agent', false)),
          count('manager_signoffs', (q) => q.eq('status', 'approved')),
          count('method_contributions', (q) => q.eq('status', 'new')),
          count('method_contributions', (q) => q.eq('status', 'applied')),
          count('storefront_reports'),
          count('chat_messages'),
          // Answers of his that have changed nothing yet. The only figure on
          // this page that is a debt rather than a total.
          count('admin_questions', (q) => q.eq('status', 'answered').is('applied_at', null)),
          count('manager_review', (q) => q.eq('status', 'pending')),
        ])
      setS({ loading: false, stores, locations, people, signoffs, waiting, confirmed, reports, coached, unapplied, mgrPending })
    } catch {
      setS({ loading: false, failed: true })
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const l = s.loading
  return (
    <VStack align="stretch" spacing={10} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          Operator
        </Text>
        <Text textStyle="pageTitle">The business</Text>
      </Box>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacingX={6} spacingY={9}>
        <Figure loading={l} value={s.stores} label="Dealerships"
          note={l ? '' : `${s.locations} location${s.locations === 1 ? '' : 's'}`} />
        <Figure loading={l} value={s.people} label="People with an account" />
        <Figure loading={l} value={s.signoffs} label="Manager sign offs"
          note="The number that says training stuck" />
        <Figure loading={l} value={s.reports} label="Storefront reports" />
      </SimpleGrid>

      <Box>
        <Text textStyle="eyebrow" color="inkMuted" mb={5}>
          The method
        </Text>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacingX={6} spacingY={9}>
          <Figure loading={l} value={s.confirmed} label="Word tracks confirmed"
            note="In Jazz's own words, approved by him" />
          <Figure loading={l} value={s.waiting} label="Waiting on Jazz"
            note="Captured from his practice sessions" />
          <Figure loading={l} value={s.coached} label="Coach messages" />
          <Figure loading={l} value={s.unapplied} label="Answered, not built"
            note="He has told us and we have not used it" />
          <Figure loading={l} value={s.mgrPending} label="Manager lines unsettled"
            note="Blocks the whole manager level" />
        </SimpleGrid>
      </Box>

      <Box>
        <Text textStyle="eyebrow" color="inkMuted" mb={1}>
          Go to
        </Text>
        <Flex direction="column">
          {DESTINATIONS.map((d) => (
            <Destination key={d.to} item={d} />
          ))}
        </Flex>
      </Box>

      {s.failed && (
        <Text fontSize="body-sm" color="inkMuted">
          Could not read the latest numbers. Reload to try again.
        </Text>
      )}
    </VStack>
  )
}
